import { HttpClient, HttpParams } from '@angular/common/http'
import { inject, Injectable } from '@angular/core'
import { firstValueFrom } from 'rxjs'
import { ApiUrlService } from './api-url.service'

@Injectable({
    providedIn: 'root'
})
export class HttpService {
    private inputParams: Record<string, string | number | boolean | string[]> = {}
    private methodType = ''
    private optionsValue: Record<string, string> = {}
    private urlValue = ''

    public readonly getUrl: ApiUrlService = inject(ApiUrlService)
    private readonly _http: HttpClient = inject(HttpClient)

    private params<T1>(inputParams: T1): this {
        this.inputParams = inputParams as Record<string, string | number | boolean | string[]>
        return this
    }

    private url(url: string): this {
        this.urlValue = url
        return this
    }

    private options<T1>(options: T1): this {
        this.optionsValue = options as Record<string, string>
        return this
    }

    private execute<T1, T2>(
        url: string,
        inputParams: T1,
        options?: Record<string, string> | Record<string, Record<string, string | string[]>> | null
    ): Promise<T2> {
        this.url(url)
        this.params(inputParams)

        if (options) {
            this.options(options)
        } else {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            this.optionsValue = {} as any
        }

        if (Object.keys(this.inputParams).length === 0 && this.methodType === 'GET') {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            this.optionsValue['params'] = null as any
        }

        if (Object.keys(this.inputParams).length > 0 && this.methodType === 'GET') {
            const httpParams = this.getHttpParams(this.inputParams)
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            this.optionsValue['params'] = httpParams as any
        }

        switch (this.methodType) {
            default:
            case 'GET':
                return firstValueFrom<T2>(this._http.get<T2>(this.urlValue, this.optionsValue))
            case 'POST':
                return firstValueFrom<T2>(this._http.post<T2>(this.urlValue, this.inputParams, this.optionsValue))
            case 'PUT':
                return firstValueFrom<T2>(this._http.put<T2>(this.urlValue, this.inputParams, this.optionsValue))
            case 'PATCH':
                return firstValueFrom<T2>(this._http.patch<T2>(this.urlValue, this.inputParams, this.optionsValue))
            case 'DELETE':
                return firstValueFrom<T2>(this._http.delete<T2>(this.urlValue, this.optionsValue))
        }
    }

    private getHttpParams(params: Record<string, string | number | boolean | string[]>): HttpParams {
        let httpParams = new HttpParams()
        for (const param in params) {
            if (Array.isArray(params[param])) {
                const paramArr: string[] = params[param] as string[]
                paramArr.forEach((val: string | number | boolean) => {
                    httpParams = httpParams.append(param, val)
                })
            } else {
                httpParams = httpParams.set(param, params[param].toString())
            }
        }

        return httpParams
    }

    public get<T1, T2>(
        url: string,
        inputParams: T1,
        options?: Record<string, string> | null | Record<string, Record<string, string | string[]>>
    ): Promise<T2> {
        this.methodType = 'GET'
        return this.execute<T1, T2>(url, inputParams, options)
    }

    public post<T1, T2>(
        url: string,
        inputParams: T1,
        options?: Record<string, string> | Record<string, Record<string, string | string[]>> | null
    ): Promise<T2> {
        this.methodType = 'POST'
        return this.execute<T1, T2>(url, inputParams, options)
    }

    public put<T1, T2>(
        url: string,
        inputParams: T1,
        options?: Record<string, string> | Record<string, Record<string, string | string[]>> | null
    ): Promise<T2> {
        this.methodType = 'PUT'
        return this.execute<T1, T2>(url, inputParams, options)
    }

    public patch<T1, T2>(
        url: string,
        inputParams: T1,
        options?: Record<string, string> | Record<string, Record<string, string | string[]>> | null
    ): Promise<T2> {
        this.methodType = 'PATCH'
        return this.execute<T1, T2>(url, inputParams, options)
    }

    public delete<T1, T2>(
        url: string,
        inputParams: T1,
        options?: Record<string, string> | Record<string, Record<string, string | string[]>> | null
    ): Promise<T2> {
        this.methodType = 'DELETE'
        return this.execute<T1, T2>(url, inputParams, options)
    }
}
